import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Mail, Phone, MapPin, Loader2 } from "lucide-react";

const inputCls = "w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition";

export default function Contact() {
  const [f, set] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", f);
      toast.success("Message received. We'll be in touch shortly.");
      set({ full_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="fade-in">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
        <div className="njas-eyebrow">Get in Touch</div>
        <div className="njas-script text-5xl text-njas-sage-deep mt-6">We're listening.</div>
        <h1 className="njas-display text-6xl sm:text-8xl text-njas-navy mt-2 leading-[0.88]">
          SAY&nbsp;HELLO.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-njas-muted leading-relaxed">
          For general enquiries, partnerships, media or grant applications. For client support, please use the referral form.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-8">
          <ContactBlock icon={Mail} label="Email" main="info@njas.org.au" sub="Referrals, partnerships and general enquiries" href="mailto:info@njas.org.au" />
          <ContactBlock icon={MapPin} label="Based in" main="19/103 George Street" sub="Parramatta NSW 2150 · Australia" />

          <div className="border-l-2 border-njas-sage pl-6">
            <div className="njas-eyebrow">In crisis?</div>
            <p className="mt-3 text-sm text-njas-text leading-relaxed">
              Call Lifeline <strong>13&nbsp;11&nbsp;14</strong> or <strong>000</strong> if you or someone else is in immediate danger.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 space-y-8 lg:pl-16 lg:border-l lg:border-[var(--njas-sage)]/50" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Full name <span className="text-njas-sage">*</span></span>
              <input required value={f.full_name} onChange={upd("full_name")} className={inputCls} data-testid="contact-name" />
            </label>
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Email <span className="text-njas-sage">*</span></span>
              <input required type="email" value={f.email} onChange={upd("email")} className={inputCls} data-testid="contact-email" />
            </label>
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Phone</span>
              <input value={f.phone} onChange={upd("phone")} className={inputCls} data-testid="contact-phone" />
            </label>
            <label className="block">
              <span className="njas-eyebrow text-njas-navy block">Subject <span className="text-njas-sage">*</span></span>
              <input required value={f.subject} onChange={upd("subject")} className={inputCls} data-testid="contact-subject" />
            </label>
          </div>
          <label className="block">
            <span className="njas-eyebrow text-njas-navy block">Message <span className="text-njas-sage">*</span></span>
            <textarea required rows={6} value={f.message} onChange={upd("message")} className={inputCls} data-testid="contact-message" />
          </label>
          <button type="submit" disabled={busy} data-testid="contact-submit" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-flex items-center gap-3 bg-njas-navy text-njas-bg px-8 py-4 hover:bg-njas-navy-hover disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send Message
          </button>
        </form>
      </section>
    </div>
  );
}

function ContactBlock({ icon: Icon, label, main, sub, href }) {
  return (
    <div className="flex items-start gap-5">
      <div className="njas-badge shrink-0"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="njas-eyebrow text-njas-sage-deep">{label}</div>
        <div className="mt-1 njas-display text-2xl text-njas-navy leading-tight">
          {href ? <a href={href} className="hover:text-njas-sage-deep transition-colors">{main}</a> : main}
        </div>
        <div className="mt-2 text-xs text-njas-muted" dangerouslySetInnerHTML={{ __html: sub }} />
      </div>
    </div>
  );
}
